/**
 * Session Timeout Manager
 * Handles inactivity-based auto logout functionality
 */
class SessionTimeoutManager {
    constructor(callbacks, config) {
        this.warningShown = false;
        this.timeoutId = null;
        this.isActive = false;
        // Default configuration: 30 minutes timeout, 5 minute warning
        this.defaultConfig = {
            timeoutMinutes: 30,
            warningMinutes: 5,
            checkIntervalSeconds: 60, // Check every minute
        };
        this.config = { ...this.defaultConfig, ...config };
        this.callbacks = callbacks;
        this.lastActivityTime = Date.now();
        this.handleActivity = this.handleActivity.bind(this);
        this.checkSession = this.checkSession.bind(this);
    }
    /**
     * Start monitoring user activity and session timeout
     */
    start() {
        if (this.isActive)
            return;
        this.isActive = true;
        this.lastActivityTime = Date.now();
        this.warningShown = false;
        // Add event listeners for user activity
        this.addActivityListeners();
        // Start periodic session checking
        this.timeoutId = setInterval(this.checkSession, this.config.checkIntervalSeconds * 1000);
        console.log(`Session timeout started: ${this.config.timeoutMinutes} minute timeout`);
    }
    /**
     * Stop monitoring and clear timers
     */
    stop() {
        if (!this.isActive)
            return;
        this.isActive = false;
        this.removeActivityListeners();
        if (this.timeoutId) {
            clearInterval(this.timeoutId);
            this.timeoutId = null;
        }
        console.log('Session timeout stopped');
    }
    /**
     * Reset the session timer (call when user activity detected)
     */
    resetTimer() {
        this.lastActivityTime = Date.now();
        this.warningShown = false;
    }
    /**
     * Get remaining session time in minutes
     */
    getRemainingMinutes() {
        const elapsed = Date.now() - this.lastActivityTime;
        const remaining = (this.config.timeoutMinutes * 60 * 1000) - elapsed;
        return Math.max(0, Math.floor(remaining / (60 * 1000)));
    }
    /**
     * Check if session should timeout or show warning
     */
    checkSession() {
        if (!this.isActive)
            return;
        const elapsedMinutes = (Date.now() - this.lastActivityTime) / (60 * 1000);
        const remainingMinutes = this.config.timeoutMinutes - elapsedMinutes;
        // Session timeout - logout user
        if (remainingMinutes <= 0) {
            console.log('Session timeout reached - logging out user');
            this.stop();
            this.callbacks.onTimeout();
            return;
        }
        // Show warning if approaching timeout
        if (remainingMinutes <= this.config.warningMinutes && !this.warningShown) {
            this.warningShown = true;
            console.log(`Session timeout warning: ${Math.ceil(remainingMinutes)} minutes remaining`);
            this.callbacks.onWarning(Math.ceil(remainingMinutes));
        }
    }
    /**
     * Handle user activity events
     */
    handleActivity() {
        if (!this.isActive)
            return;
        this.resetTimer();
    }
    /**
     * Add event listeners for user activity detection
     */
    addActivityListeners() {
        const events = [
            'mousedown',
            'mousemove',
            'keydown',
            'scroll',
            'touchstart',
            'click',
        ];
        events.forEach(event => {
            document.addEventListener(event, this.handleActivity, { passive: true });
        });
        // Also listen for API requests as activity
        window.addEventListener('beforeunload', this.handleActivity);
    }
    /**
     * Remove activity event listeners
     */
    removeActivityListeners() {
        const events = [
            'mousedown',
            'mousemove',
            'keydown',
            'scroll',
            'touchstart',
            'click',
        ];
        events.forEach(event => {
            document.removeEventListener(event, this.handleActivity);
        });
        window.removeEventListener('beforeunload', this.handleActivity);
    }
    /**
     * Extend session (reset timer)
     */
    extendSession() {
        this.resetTimer();
        console.log('Session extended');
    }
    /**
     * Get session status
     */
    getStatus() {
        return {
            isActive: this.isActive,
            remainingMinutes: this.getRemainingMinutes(),
            warningShown: this.warningShown,
            lastActivity: new Date(this.lastActivityTime),
        };
    }
}
// Singleton instance
let sessionTimeoutManager = null;
/**
 * Initialize session timeout manager
 */
export const initializeSessionTimeout = (callbacks, config) => {
    if (sessionTimeoutManager) {
        sessionTimeoutManager.stop();
    }
    sessionTimeoutManager = new SessionTimeoutManager(callbacks, config);
    return sessionTimeoutManager;
};
/**
 * Get current session timeout manager instance
 */
export const getSessionTimeoutManager = () => {
    return sessionTimeoutManager;
};
/**
 * Reset session timer (for external API calls)
 */
export const resetSessionTimer = () => {
    if (sessionTimeoutManager) {
        sessionTimeoutManager.resetTimer();
    }
};
/**
 * Stop session timeout monitoring
 */
export const stopSessionTimeout = () => {
    if (sessionTimeoutManager) {
        sessionTimeoutManager.stop();
        sessionTimeoutManager = null;
    }
};
